import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Row, Col, Button, Badge, Alert, Spinner, InputGroup } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import AddressAutocompleteInput from "../multiOrders/AddressAutocompleteInput";
import CompanySelectComponent from "../multiOrders/CompanySelectComponent";
import AddCompanyComponent from "../multiOrders/AddCompanyComponent";
import AddCustomerComponent from "../multiOrders/AddCustomerComponent";
import AddAddressComponent from "../multiOrders/AddAddressComponent";
import EvenementClientSelect from "./EvenementClientSelect";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import ClientFicheModal from "app/orderTool/components/ClientFicheModal";
import { initialize } from "state/adminData/actions";
import { fetchGabarits } from "state/gabarit/actions";
import {
  createEvenement,
  updateEvenement,
  patchEvenementStatut,
  transformEvenement,
  addEvenementNote,
  addEvenementPieceJointe,
} from "state/evenement/actions";

// Repli si les constantes ne sont pas encore chargées (la liste vient de Evenement::CHARGES_DE_CLIENTELE).
const CHARGES_DEFAUT = ["Julie", "Valentin", "Camille"];

const formatNoteDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return d;
  }
};

const EMPTY_FORM = {
  charge_de_clientele: "",
  source: "",
  type_client: "",
  secteur_activite: "",
  entreprise_id: null,
  client_id: null,
  objectif: "",
  nature: "",
  typologie: [],
  ambiance: "",
  theme: "",
  nom: "",
  date_evenement: "",
  // Événement sur plusieurs jours : dates en plus de date_evenement (une prestation par date et par format)
  dates_supplementaires: [],
  nb_convives: "",
  adresse_livraison: "",
  // Adresse du carnet choisie : le devis reprend ses commentaires livraison/récup.
  adresse_livraison_carnet_id: null,
  // Gabarit de devis appliqué à la première ouverture du devis issu de la transformation
  gabarit_id: null,
  adresse_facturation: "",
  contact_nom: "",
  contact_telephone: "",
  contact_email: "",
  type_convives: "",
  contraintes_alimentaires: "",
  zero_dechet: false,
  deroule_estime: "",
  format_attendu: [],
  boissons_softs: "",
  alcool: false,
  alcool_detail: "",
  materiel_mobilier: "",
  nb_verres_par_personne: "",
  service: false,
  budget_centimes: "",
  budget_par_personne: "",
  budget_base: "",
  mise_en_concurrence: false,
  concurrents: "",
  criteres_choix: [],
  mauvaises_experiences: "",
  date_decision: "",
  decisionnaire: "",
  rdv_physique_propose: false,
  visite_site_proposee: false,
  degustation_proposee: false,
  // Suivi du dossier (relances)
  date_envoi_devis: "",
  relance_1_faite: false,
  relance_1_date: "",
  relance_2_faite: false,
  relance_2_date: "",
  relance_3_faite: false,
  relance_3_date: "",
};

/**
 * Questionnaire guidé de qualification d'un événement B2B (4 sections :
 * connaissance client / événement / offre / leviers) + transformation
 * en commande groupée quand les champs minimaux sont remplis.
 */
const EvenementModal = ({ show, evenement, prefill, constants, chargeParDefaut, loadingDetail, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const companies = useSelector((state) => state.adminData.companies || []);
  const addresses = useSelector((state) => state.adminData.addresses || []);
  const gabarits = useSelector((state) => state.gabarit?.list || []);
  const gabaritsInitialises = useSelector((state) => state.gabarit?.isInitialized);
  const gabaritsEnCours = useSelector((state) => state.gabarit?.loading);
  // Choix d'un gabarit proposé au clic sur « Transformer » quand la fiche n'en a pas
  const [choixGabarit, setChoixGabarit] = useState({ ouvert: false, id: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [entrepriseNom, setEntrepriseNom] = useState("");
  const [clientNom, setClientNom] = useState("");
  const [saving, setSaving] = useState(false);
  const [transforming, setTransforming] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [notes, setNotes] = useState([]);
  const [piecesJointes, setPiecesJointes] = useState([]);
  const [savingPj, setSavingPj] = useState(false);
  // Source : select + précision libre pour « Autres », round-trip via le champ
  // texte `source`. Les choix viennent de l'API (Evenement::SOURCES).
  const sourceChoices = constants?.sources || [];
  const sourceAutres = constants?.sourceAutres || "Autres";
  const [sourceChoice, setSourceChoice] = useState("");
  const [sourcePrecision, setSourcePrecision] = useState("");
  const [ficheOpen, setFicheOpen] = useState(false);
  // Id de l'événement créé depuis cette fenêtre : sans lui, un second
  // « Enregistrer » recréait un événement au lieu de mettre à jour le premier.
  const [createdId, setCreatedId] = useState(null);
  const persistingRef = useRef(false);
  const currentId = evenement?.id || createdId;

  useEffect(() => {
    setCreatedId(null);
  }, [show, evenement, prefill]);

  useEffect(() => {
    setNotes(evenement?.notes || []);
    setPiecesJointes(evenement?.pieces_jointes || []);
  }, [evenement]);

  // Charge le carnet d'adresses / clients / entreprises (comme l'éditeur de devis)
  // dès l'ouverture, pour alimenter l'autosuggest et les selects.
  useEffect(() => {
    if (show) {
      dispatch(initialize(true));
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) {
      return;
    }
    // En édition : `evenement`. En création pré-remplie depuis un devis : `prefill`.
    const seed = evenement || prefill;
    if (seed) {
      setForm({
        ...EMPTY_FORM,
        ...Object.fromEntries(
          Object.keys(EMPTY_FORM).map((key) => {
            let value = seed[key];
            if (key === "entreprise_id") {
              value = seed.entreprise?.id || null;
            }
            if (key === "client_id") {
              value = seed.client?.id || null;
            }
            // Les champs date sont rendus par des <input type="date"> : on ne
            // garde que la partie AAAA-MM-JJ, quelle que soit la forme reçue.
            if (key.startsWith("date_") || /^relance_\d_date$/.test(key)) {
              if (value) {
                value = String(value).slice(0, 10);
              }
            }
            if (value === null || value === undefined) {
              value = EMPTY_FORM[key];
            }
            return [key, value];
          })
        ),
      });
      setEntrepriseNom(seed.entreprise?.name || "");
      setClientNom(
        seed.client
          ? `${seed.client.lastname || ""} ${seed.client.firstname || ""}`.trim()
          : ""
      );
      const src = seed.source || "";
      const known = (constants?.sources || []).some((s) => s.value === src);
      if (known) { setSourceChoice(src); setSourcePrecision(""); }
      else if (src) { setSourceChoice(constants?.sourceAutres || "Autres"); setSourcePrecision(src); }
      else { setSourceChoice(""); setSourcePrecision(""); }
    } else {
      // Nouvel événement : chargé de clientèle du filtre du board s'il y en a un (retour Keep #22,
      // relu par Romain le 30/10), sinon celui de l'admin connecté.
      setForm({ ...EMPTY_FORM, charge_de_clientele: chargeParDefaut || constants?.chargeConnecte || "" });
      setEntrepriseNom("");
      setClientNom("");
      setSourceChoice("");
      setSourcePrecision("");
    }
  }, [show, evenement, prefill, constants, chargeParDefaut]);

  // Devis lié (édition d'un événement transformé, ou création depuis un devis orphelin).
  const commandeGroupee = evenement?.commande_groupee || prefill?.commande_groupee || null;

  // Adresse texte « street_number street, postal_code city » depuis un client/adresse.
  const buildAddressText = (a) =>
    [a?.street_number, a?.street, a?.postal_code, a?.city]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

  // Sélection d'un client → autofill contact + entreprise + adresse de facturation.
  const fillFromCustomer = (customer) => {
    if (!customer) return;
    const nom = `${customer.lastname || ""} ${customer.firstname || ""}`.trim();
    setClientNom(nom);
    setForm((f) => {
      const next = {
        ...f,
        client_id: customer.id || null,
        contact_nom: nom || f.contact_nom,
        contact_telephone: customer.phone || f.contact_telephone,
        contact_email: customer.email || f.contact_email,
      };
      const addr = buildAddressText(customer);
      if (addr) next.adresse_facturation = addr;
      const company = companies.find((c) => c.id === customer.company_id);
      if (company) {
        next.entreprise_id = company.id;
        setEntrepriseNom(company.name);
      }
      return next;
    });
  };

  // Client créé depuis la fiche : on reprend aussi son adresse de facturation, pour ne
  // pas la ressaisir sur la demande (retour terrain Keep 26/10). La création d'un client
  // ne demande plus d'adresse de livraison (carte Trello WMs93rwA) : celle de la demande
  // se saisit ici.
  const fillFromCreatedCustomer = (customer, customerInfo) => {
    fillFromCustomer(customer);
    if (!customerInfo) return;
    const facturation = buildAddressText(customerInfo);
    if (facturation) {
      setForm((f) => ({ ...f, adresse_facturation: facturation }));
    }
  };

  // Options react-select pour le carnet d'adresses.
  const addressOptions = addresses.map((a) => ({
    value: a.id,
    label: [a.name_address, buildAddressText(a)].filter(Boolean).join(" — "),
    text: buildAddressText(a),
  }));

  // Combine select + précision dans le champ `source` enregistré.
  const onSourceChoice = (choice) => {
    setSourceChoice(choice);
    if (choice === sourceAutres) {
      set("source", sourcePrecision || "");
    } else {
      set("source", choice);
      setSourcePrecision("");
    }
  };
  const onSourcePrecision = (val) => {
    setSourcePrecision(val);
    set("source", val);
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleInArray = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const formats = constants?.formatsAttendus || [];
  const criteres = constants?.criteresChoix || [];

  // Champs minimaux manquants, calculés côté front pour piloter le bouton
  // Transformer (la vérité reste côté back via missing_fields)
  const missing = [];
  if (!form.date_evenement) missing.push("date de l'événement");
  if (!form.nb_convives || parseInt(form.nb_convives, 10) <= 0) missing.push("nb convives");
  if (!form.adresse_livraison) missing.push("adresse de livraison");
  if (!form.format_attendu.length) missing.push("format attendu");
  if (!(form.contact_nom && (form.contact_email || form.contact_telephone))) missing.push("commanditaire (nom + email ou tél)");

  // Garde e-commerce : formats attendus limités aux plateaux repas
  // (la typologie ne se saisit plus, cf. retour Camille 15/09)
  const ecommerceOnly =
    form.format_attendu.length > 0 &&
    // Seuls les plateaux repas se commandent en ligne : un petit déjeuner seul reste un
    // devis sur mesure (réponse de Camille au retour Keep #11, 30/10/2026).
    form.format_attendu.every((f) => f === "plateaux repas");

  const aboveThreshold = form.budget_centimes && parseInt(form.budget_centimes, 10) >= (constants?.seuilJalonsCentimes || 500000);

  useEffect(() => {
    if (show && !gabaritsInitialises && !gabaritsEnCours) {
      dispatch(fetchGabarits());
    }
  }, [show, gabaritsInitialises, gabaritsEnCours, dispatch]);

  const buildPayload = () => ({
    ...form,
    nb_convives: form.nb_convives === "" ? null : parseInt(form.nb_convives, 10),
    nb_verres_par_personne: form.nb_verres_par_personne === "" ? null : parseInt(form.nb_verres_par_personne, 10),
    budget_centimes: form.budget_centimes === "" ? null : parseInt(form.budget_centimes, 10),
    budget_par_personne: form.budget_par_personne === "" ? null : parseInt(form.budget_par_personne, 10),
    budget_base: form.budget_base || null,
    date_evenement: form.date_evenement || null,
    dates_supplementaires: (form.dates_supplementaires || []).filter(Boolean),
    date_decision: form.date_decision || null,
    date_envoi_devis: form.date_envoi_devis || null,
    relance_1_date: form.relance_1_date || null,
    relance_2_date: form.relance_2_date || null,
    relance_3_date: form.relance_3_date || null,
    // Création depuis un devis orphelin : lie l'événement au devis existant.
    ...(!currentId && prefill?.commande_groupee?.id
      ? { commande_groupee_id: prefill.commande_groupee.id }
      : {}),
  });

  const persistEvenement = async ({ quiet = false, extra = {} } = {}) => {
    // Origine de la demande obligatoire depuis le 29/10/2026 : c'est la seule source du
    // bloc « origine des nouveaux clients » du pilotage commercial (le back la refuse aussi).
    if (!(form.source || "").trim()) {
      toast.error("Renseigne la source de la demande avant d'enregistrer");
      return null;
    }
    // Verrou synchrone : deux clics rapprochés n'envoient qu'une requête.
    if (persistingRef.current) {
      return null;
    }
    persistingRef.current = true;
    let resp;
    try {
      const payload = { ...buildPayload(), ...extra };
      const action = currentId
        ? updateEvenement(currentId, payload)
        : createEvenement(payload);
      resp = await dispatch(action);
    } finally {
      persistingRef.current = false;
    }
    if (resp?.error || resp?.success === false) {
      if (!quiet) {
        toast.error(resp?.message || "Erreur à l'enregistrement");
      }
      return null;
    }
    if (!currentId && resp?.evenement?.id) {
      setCreatedId(resp.evenement.id);
    }
    if (!quiet) {
      toast.success("Événement enregistré");
      onSaved && onSaved();
    }
    return resp?.evenement || (currentId ? { id: currentId } : null);
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await persistEvenement();
    setSaving(false);
    return result;
  };

  const handlePerdu = async () => {
    const confirm = await swal({
      title: "Marquer cet événement comme perdu ?",
      icon: "warning",
      buttons: ["Annuler", "Marquer perdu"],
    });
    if (!confirm || !evenement?.id) {
      return;
    }
    await dispatch(patchEvenementStatut(evenement.id, "PERDU"));
    toast.info("Événement marqué perdu");
    onSaved && onSaved();
    onClose();
  };

  // Clic sur « Transformer » : sans gabarit choisi sur la fiche, on en propose un d'abord.
  const handleTransform = () => {
    if (!form.gabarit_id && gabarits.length > 0) {
      setChoixGabarit({ ouvert: true, id: "" });
      return;
    }
    transformer(form.gabarit_id || null);
  };

  const transformer = async (gabaritId) => {
    setChoixGabarit({ ouvert: false, id: "" });
    if (gabaritId !== form.gabarit_id) {
      set("gabarit_id", gabaritId);
    }
    setTransforming(true);
    const saved = await persistEvenement({ quiet: true, extra: { gabarit_id: gabaritId } });
    const id = saved?.id || currentId;
    if (!id) {
      setTransforming(false);
      toast.error("Enregistrez d'abord l'événement");
      return;
    }
    const resp = await dispatch(transformEvenement(id));
    setTransforming(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Transformation impossible");
      return;
    }
    const groupedId = resp?.commande_groupee?.id;
    toast.success("Commande groupée créée !");
    onSaved && onSaved();
    onClose();
    if (groupedId) {
      history.push(`/commande?order_id=${groupedId}&order_type=v2&from=pipeline`);
    }
  };

  const euros = (centimes) => (centimes === "" || centimes === null ? "" : (parseInt(centimes, 10) / 100).toString());

  // Budget total <> budget / personne : la saisie de l'un recalcule l'autre via le nb de convives.
  const eurosToCents = (value) => (value === "" ? "" : Math.round(parseFloat(value.replace(",", ".") || 0) * 100));
  const onBudgetTotal = (value) => {
    const cents = eurosToCents(value);
    setForm((f) => {
      const conv = parseInt(f.nb_convives, 10) || 0;
      const next = { ...f, budget_centimes: cents };
      if (conv > 0 && cents !== "") next.budget_par_personne = Math.round(cents / conv);
      return next;
    });
  };
  const onBudgetPerPerson = (value) => {
    const cents = eurosToCents(value);
    setForm((f) => {
      const conv = parseInt(f.nb_convives, 10) || 0;
      const next = { ...f, budget_par_personne: cents };
      if (conv > 0 && cents !== "") next.budget_centimes = cents * conv;
      return next;
    });
  };

  const formDisabled = loadingDetail || saving || transforming;

  // Retour Camille 02/07 : « dans la partie suivi événement - on peut ajouter des
  // PJ genre pdf ? ». L'API existait déjà, seul le front ne l'exposait pas.
  const handleAddPieceJointe = async (fichier) => {
    if (!fichier || !evenement?.id) return;
    setSavingPj(true);
    const resp = await dispatch(addEvenementPieceJointe(evenement.id, { type: "DOCUMENT", fichier }));
    setSavingPj(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Impossible d'ajouter la pièce jointe");
      return;
    }
    toast.success("Pièce jointe ajoutée");
    if (resp?.piece_jointe) {
      setPiecesJointes((prev) => [...prev, resp.piece_jointe]);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !evenement?.id) return;
    setSavingNote(true);
    const resp = await dispatch(addEvenementNote(evenement.id, noteText.trim()));
    setSavingNote(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Impossible d'ajouter la note");
      return;
    }
    toast.success("Note ajoutée");
    setNoteText("");
    if (resp?.evenement?.notes) {
      setNotes(resp.evenement.notes);
    }
    onSaved && onSaved();
  };

  return (
    <>
    <Modal show={show} onHide={onClose} dialogClassName="modal-xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {evenement?.reference ? `Événement ${evenement.reference}` : "Nouvel événement"}{" "}
          {evenement?.statut && <Badge variant={evenement.statut === "A_QUALIFIER" ? "warning" : "secondary"}>{evenement.statut}</Badge>}
          {loadingDetail && (
            <Spinner animation="border" size="sm" variant="success" className="ml-2" role="status" />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay show={loadingDetail} message="Chargement du questionnaire…" minHeight={200} />
        <fieldset disabled={formDisabled} style={{ border: "none", margin: 0, padding: 0, opacity: formDisabled && !loadingDetail ? 0.65 : 1 }}>
        {ecommerceOnly && (
          <Alert variant="warning">
            Plateaux repas seuls : orienter le client vers la <b>commande e-commerce</b> (lemoulin.fr) plutôt que le devis sur mesure.
          </Alert>
        )}
        {(evenement?.id || commandeGroupee?.id) && (
          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
            {commandeGroupee?.id && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => { onClose(); history.push(`/commande?order_id=${commandeGroupee.id}&order_type=v2&from=pipeline`); }}
              >
                ↗ Ouvrir l'édition du devis (M/{commandeGroupee.id})
              </Button>
            )}
            {(evenement?.client?.id || evenement?.entreprise?.id) && (
              <Button size="sm" variant="outline-secondary" onClick={() => setFicheOpen(true)}>
                👤 Fiche contact & entreprise
              </Button>
            )}
          </div>
        )}
        <Row>
          <Col md={6}>
            <h5>1. Connaissance client</h5>
            <Form.Group>
              <Form.Label>Chargé de clientèle</Form.Label>
              <Form.Control as="select" value={form.charge_de_clientele} onChange={(e) => set("charge_de_clientele", e.target.value)}>
                <option value="">Non assigné</option>
                {(constants?.chargesDeClientele || CHARGES_DEFAUT).map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Client (commanditaire)</Form.Label>
              <InputGroup>
                <div style={{ flex: 1 }}>
                  <EvenementClientSelect value={clientNom} onSelect={fillFromCustomer} />
                </div>
                <InputGroup.Append>
                  <AddCustomerComponent onCreated={fillFromCreatedCustomer} />
                </InputGroup.Append>
              </InputGroup>
              {form.client_id && (
                <Form.Text className="text-muted">
                  Client lié (ID {form.client_id}) — contact &amp; entreprise pré-remplis.{" "}
                  <Button variant="link" size="sm" className="p-0 align-baseline"
                    onClick={() => { set("client_id", null); setClientNom(""); }}>
                    détacher
                  </Button>
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Entreprise</Form.Label>
              <InputGroup>
                <div style={{ flex: 1 }}>
                  <CompanySelectComponent
                    selected={entrepriseNom ? { name: entrepriseNom } : null}
                    onSelect={(company) => { set("entreprise_id", company.id); setEntrepriseNom(company.name); }}
                  />
                </div>
                <InputGroup.Append>
                  <AddCompanyComponent
                    onCreated={(company) => { set("entreprise_id", company.id); setEntrepriseNom(company.name); }}
                  />
                </InputGroup.Append>
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>Type de client</Form.Label>
              <Form.Control as="select" value={form.type_client} onChange={(e) => set("type_client", e.target.value)}>
                <option value="">—</option>
                {(constants?.typesClient || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Source de la demande *</Form.Label>
              <Form.Control as="select" value={sourceChoice} onChange={(e) => onSourceChoice(e.target.value)}>
                <option value="">—</option>
                {sourceChoices.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                <option value={sourceAutres}>{sourceAutres}</option>
              </Form.Control>
              {sourceChoice === sourceAutres && (
                <Form.Control className="mt-1" placeholder="Préciser…" value={sourcePrecision} onChange={(e) => onSourcePrecision(e.target.value)} />
              )}
              {sourceChoice === sourceAutres && (
                <Form.Text className="text-muted">
                  Une précision libre ne remonte pas dans le suivi des origines : préférer un
                  choix de la liste dès que l'un d'eux convient.
                </Form.Text>
              )}
            </Form.Group>

            <h5 className="mt-3">2. Événement</h5>
            <Form.Group>
              <Form.Label>Nom de l'événement</Form.Label>
              <Form.Control value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} placeholder="ex. Séminaire annuel, Soirée des 10 ans…" />
              <Form.Text className="text-muted">Repris dans le commentaire visible sur le devis et la facture.</Form.Text>
            </Form.Group>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Date de l'événement *</Form.Label>
                  <Form.Control type="date" value={form.date_evenement} onChange={(e) => set("date_evenement", e.target.value)} />
                  {(form.dates_supplementaires || []).map((d, i) => (
                    <InputGroup key={i} size="sm" className="mt-1">
                      <Form.Control
                        type="date"
                        value={d}
                        onChange={(e) => {
                          const value = e.target.value;
                          setForm((f) => ({ ...f, dates_supplementaires: f.dates_supplementaires.map((x, j) => (j === i ? value : x)) }));
                        }}
                      />
                      <InputGroup.Append>
                        <Button
                          variant="outline-secondary"
                          title="Retirer cette date"
                          onClick={() => setForm((f) => ({ ...f, dates_supplementaires: f.dates_supplementaires.filter((_, j) => j !== i) }))}
                        >×</Button>
                      </InputGroup.Append>
                    </InputGroup>
                  ))}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={() => setForm((f) => ({ ...f, dates_supplementaires: [...(f.dates_supplementaires || []), ""] }))}
                  >+ Ajouter une date</Button>
                  {(form.dates_supplementaires || []).filter(Boolean).length > 0 && (
                    <Form.Text className="text-muted">Au passage en devis : une prestation par date et par format attendu.</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Nb convives *</Form.Label>
                  <Form.Control type="number" min="1" value={form.nb_convives} onChange={(e) => set("nb_convives", e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Objectif</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.objectif} onChange={(e) => set("objectif", e.target.value)} />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Nature</Form.Label>
                  <Form.Control as="select" value={form.nature} onChange={(e) => set("nature", e.target.value)}>
                    <option value="">—</option>
                    {(constants?.natures || []).map((n) => <option key={n} value={n}>{n}</option>)}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Ambiance</Form.Label>
                  <Form.Control as="select" value={form.ambiance} onChange={(e) => set("ambiance", e.target.value)}>
                    <option value="">—</option>
                    {(constants?.ambiances || []).map((a) => <option key={a} value={a}>{a}</option>)}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            {/* La section « Typologie » a été retirée du brief : elle faisait
                doublon avec la partie « Offre » (retour Camille 15/09). Le champ
                reste en base et alimente l'historique ; seule la saisie disparaît. */}
            <Form.Group>
              <Form.Label>
                Typologie convives{" "}
                <span title="Profil des convives — ex. gros mangeur, jeune sportif, homme, femme…" style={{ cursor: "help", color: "#888" }}>(?)</span>
              </Form.Label>
              <Form.Control value={form.type_convives} onChange={(e) => set("type_convives", e.target.value)} placeholder="ex. gros mangeur, jeune sportif…" />
            </Form.Group>
            <Form.Group>
              <Form.Label className="d-flex justify-content-between align-items-center">
                <span>Adresse de livraison *</span>
                <AddAddressComponent title="+ carnet" />
              </Form.Label>
              {addressOptions.length > 0 && (
                <Select
                  className="mb-1"
                  classNamePrefix="rs"
                  placeholder="Carnet d'adresses…"
                  isClearable
                  options={addressOptions}
                  value={addressOptions.find((o) => o.value === form.adresse_livraison_carnet_id) || null}
                  onChange={(opt) =>
                    setForm((f) => ({
                      ...f,
                      adresse_livraison: opt ? opt.text : f.adresse_livraison,
                      adresse_livraison_carnet_id: opt ? opt.value : null,
                    }))
                  }
                />
              )}
              <AddressAutocompleteInput
                onSelect={(address) => setForm((f) => ({ ...f, adresse_livraison: address.label, adresse_livraison_carnet_id: null }))}
              />
              <Form.Control
                className="mt-1"
                value={form.adresse_livraison}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((f) => ({ ...f, adresse_livraison: value, adresse_livraison_carnet_id: null }));
                }}
                placeholder="Ou saisie libre"
              />
              {form.adresse_livraison_carnet_id && (
                <Form.Text className="text-muted">
                  Adresse du carnet : ses commentaires de livraison et de récupération seront repris dans le devis.
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Déroulé estimé</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.deroule_estime} onChange={(e) => set("deroule_estime", e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Adresse de facturation</Form.Label>
              {addressOptions.length > 0 && (
                <Select
                  className="mb-1"
                  classNamePrefix="rs"
                  placeholder="Carnet d'adresses…"
                  isClearable
                  options={addressOptions}
                  value={null}
                  onChange={(opt) => opt && set("adresse_facturation", opt.text)}
                />
              )}
              <Form.Control value={form.adresse_facturation} onChange={(e) => set("adresse_facturation", e.target.value)} />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Contact — nom *</Form.Label>
                  <Form.Control value={form.contact_nom} onChange={(e) => set("contact_nom", e.target.value)} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control value={form.contact_telephone} onChange={(e) => set("contact_telephone", e.target.value)} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </Col>

          <Col md={6}>
            <h5>3. Offre</h5>
            <Form.Check className="mb-2" type="checkbox" id="zero-dechet" label="Zéro déchet" checked={form.zero_dechet} onChange={(e) => set("zero_dechet", e.target.checked)} />
            <Form.Group>
              <Form.Label>Gabarit du devis (facultatif)</Form.Label>
              <Form.Control
                as="select"
                value={form.gabarit_id || ""}
                onChange={(e) => set("gabarit_id", e.target.value ? parseInt(e.target.value, 10) : null)}
              >
                <option value="">Aucun — devis vierge</option>
                {gabarits.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Form.Control>
              <Form.Text className="text-muted">Appliqué aux prestations à la première ouverture du devis : client, adresses, dates et couverts restent ceux de la demande.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Formats attendus *</Form.Label>
              <div>
                {formats.map((f) => (
                  <Form.Check key={f} type="checkbox" id={`fmt-${f}`} label={f}
                    checked={form.format_attendu.includes(f)} onChange={() => toggleInArray("format_attendu", f)} />
                ))}
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Contraintes alimentaires</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.contraintes_alimentaires} onChange={(e) => set("contraintes_alimentaires", e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Boissons softs</Form.Label>
              <Form.Control value={form.boissons_softs} onChange={(e) => set("boissons_softs", e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Alcool</Form.Label>
              <Form.Control value={form.alcool_detail} onChange={(e) => set("alcool_detail", e.target.value)} placeholder="ex. vin, bières, champagne, nb de verres / personne…" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Matériel et mobilier</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.materiel_mobilier} onChange={(e) => set("materiel_mobilier", e.target.value)} placeholder="ex. mange-debout, vaisselle, nappage…" />
            </Form.Group>
            <Form.Check type="checkbox" id="service" label="Avec service" checked={form.service} onChange={(e) => set("service", e.target.checked)} />

            <h5 className="mt-3">4. Leviers</h5>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Budget total (€)</Form.Label>
                  <Form.Control type="number" step="0.01" value={euros(form.budget_centimes)} onChange={(e) => onBudgetTotal(e.target.value)} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Budget / personne (€)</Form.Label>
                  <Form.Control type="number" step="0.01" value={euros(form.budget_par_personne)} onChange={(e) => onBudgetPerPerson(e.target.value)} />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Budget exprimé en</Form.Label>
                  <Form.Control as="select" value={form.budget_base || ""} onChange={(e) => set("budget_base", e.target.value)}>
                    <option value="">Non précisé</option>
                    {(constants?.budgetBases || ["HT", "TTC"]).map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Form.Check type="checkbox" id="concurrence" label="Mise en concurrence" checked={form.mise_en_concurrence} onChange={(e) => set("mise_en_concurrence", e.target.checked)} />
            {form.mise_en_concurrence && (
              <Form.Group className="mt-1">
                <Form.Label>Concurrents</Form.Label>
                <Form.Control value={form.concurrents} onChange={(e) => set("concurrents", e.target.value)} />
              </Form.Group>
            )}
            <Form.Group className="mt-2">
              <Form.Label>Critères de choix</Form.Label>
              <div>
                {criteres.map((c) => (
                  <Form.Check inline key={c} type="checkbox" id={`crit-${c}`} label={c}
                    checked={form.criteres_choix.includes(c)} onChange={() => toggleInArray("criteres_choix", c)} />
                ))}
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Mauvaises expériences passées</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.mauvaises_experiences} onChange={(e) => set("mauvaises_experiences", e.target.value)} />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Objectif d'envoi (date)</Form.Label>
                  <Form.Control type="date" value={form.date_decision} onChange={(e) => set("date_decision", e.target.value)} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Décisionnaire</Form.Label>
                  <Form.Control value={form.decisionnaire} onChange={(e) => set("decisionnaire", e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            {aboveThreshold && (
              <Alert variant="info" className="mt-2">
                <b>Budget ≥ 5 000 € — jalons à proposer :</b>
                <Form.Check type="checkbox" id="rdv" label="RDV physique proposé" checked={form.rdv_physique_propose} onChange={(e) => set("rdv_physique_propose", e.target.checked)} />
                <Form.Check type="checkbox" id="visite" label="Visite de site proposée" checked={form.visite_site_proposee} onChange={(e) => set("visite_site_proposee", e.target.checked)} />
                <Form.Check type="checkbox" id="degustation" label="Dégustation proposée" checked={form.degustation_proposee} onChange={(e) => set("degustation_proposee", e.target.checked)} />
              </Alert>
            )}

            {missing.length > 0 && !prefill && (
              <Alert variant="secondary" className="mt-2">
                <b>Champs minimaux manquants pour transformer :</b> {missing.join(", ")}
              </Alert>
            )}
          </Col>
        </Row>

        {/* Suivi du dossier : les relances 1 et 2 se calent sur la date d'envoi
            du devis (J+4 / J+10), la 3ᵉ reste libre (retour Camille 15/09). */}
        <hr />
        <h6>Suivi du dossier</h6>
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.85rem" }}>Devis envoyé le</Form.Label>
              {/* Le devis part chez le client quand le chargé de clientèle le
                  décide, pas quand il l'enregistre (arbitrage Camille du 22/09).
                  La date se saisit librement — y compris dans le passé, pour
                  reprendre un devis déjà parti — et « Aujourd'hui » n'est qu'un
                  raccourci. */}
              <InputGroup size="sm">
                <Form.Control
                  type="date"
                  value={form.date_envoi_devis || ""}
                  onChange={(e) => set("date_envoi_devis", e.target.value)}
                />
                <InputGroup.Append>
                  <Button
                    variant={form.date_envoi_devis ? "outline-secondary" : "outline-primary"}
                    title="Mettre la date du jour"
                    onClick={() => set("date_envoi_devis", new Date().toISOString().slice(0, 10))}
                  >
                    Aujourd'hui
                  </Button>
                </InputGroup.Append>
              </InputGroup>
              <Form.Text className="text-muted">
                Date réelle d'envoi au client, <strong>passée si le devis est déjà parti</strong>.
                Elle donne le départ des relances ; tant qu'elle est vide, aucune n'est prévue.
                {form.date_envoi_devis && (
                  <>
                    {" "}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 align-baseline"
                      onClick={() => set("date_envoi_devis", "")}
                    >
                      Effacer
                    </Button>
                  </>
                )}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={8}>
            {[1, 2, 3].map((n) => (
              <Row key={`relance-${n}`} className="align-items-center mb-1">
                <Col xs={6}>
                  <Form.Check
                    type="checkbox"
                    id={`relance-${n}-faite`}
                    label={`Relance ${n}${n === 3 ? "" : ` (J+${n === 1 ? 4 : 10})`}`}
                    checked={!!form[`relance_${n}_faite`]}
                    onChange={(e) => set(`relance_${n}_faite`, e.target.checked)}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={form[`relance_${n}_date`] || ""}
                    onChange={(e) => set(`relance_${n}_date`, e.target.value)}
                  />
                </Col>
              </Row>
            ))}
          </Col>
        </Row>

        {evenement?.id && (
          <>
            <hr />
            <h6>Notes et échanges</h6>
            <p className="text-muted" style={{ fontSize: "0.8rem", marginBottom: 6 }}>
              Brief téléphonique, récap Ringover, ajustements demandés par le client…
            </p>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Ajouter une note (appel, échange, ajustement…)"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                disabled={savingNote}
              />
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-1"
                onClick={handleAddNote}
                disabled={savingNote || !noteText.trim()}
              >
                {savingNote ? <Spinner animation="border" size="sm" /> : "Ajouter la note"}
              </Button>
            </Form.Group>
            <Form.Group className="mt-2">
              <Form.Label className="mb-1" style={{ fontSize: "0.85rem" }}>Pièces jointes</Form.Label>
              <Form.Control
                type="file"
                size="sm"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv"
                disabled={savingPj}
                onChange={(e) => {
                  const fichier = e.target.files && e.target.files[0];
                  handleAddPieceJointe(fichier);
                  e.target.value = "";
                }}
              />
              {savingPj && <div className="mt-1"><Spinner animation="border" size="sm" /> Envoi…</div>}
              {piecesJointes.length > 0 && (
                <ul className="mt-2 mb-0" style={{ paddingLeft: "1.1em", fontSize: "0.85rem" }}>
                  {piecesJointes.map((pj, i) => (
                    <li key={pj.id || i}>
                      {pj.chemin_fichier ? (
                        <a
                          href={`${process.env.REACT_APP_API_URL}/${pj.chemin_fichier}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {pj.nom_original || "Document"}
                        </a>
                      ) : (
                        <span>{pj.nom_original || "Email collé"}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>

            {notes.length > 0 ? (
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {notes.map((n) => (
                  <div key={n.id} className="border-bottom py-1">
                    <div style={{ fontSize: "0.7rem", color: "#888" }}>
                      {formatNoteDate(n.created_at)}{n.auteur ? ` · ${n.auteur}` : ""}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>{n.contenu}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted" style={{ fontSize: "0.85rem" }}>Aucune note pour le moment.</div>
            )}
          </>
        )}
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        {evenement?.id && evenement?.statut === "A_QUALIFIER" && (
          <Button variant="outline-danger" onClick={handlePerdu} disabled={formDisabled}>Marquer perdu</Button>
        )}
        <Button variant="secondary" onClick={onClose} disabled={saving || transforming}>Fermer</Button>
        <Button variant="outline-success" onClick={handleSave} disabled={formDisabled}>
          {saving ? <><Spinner animation="border" size="sm" className="mr-1" /> Enregistrement…</> : "Enregistrer"}
        </Button>
        {((!evenement && !prefill) || evenement?.statut === "A_QUALIFIER") && (
          <Button variant="success" disabled={formDisabled || missing.length > 0} onClick={handleTransform}
            title={missing.length ? `Manque : ${missing.join(", ")}` : "Créer la commande groupée pré-remplie"}>
            {transforming ? <><Spinner animation="border" size="sm" className="mr-1" /> Transformation…</> : "Transformer en commande groupée"}
          </Button>
        )}
      </Modal.Footer>
      <Modal show={choixGabarit.ouvert} onHide={() => setChoixGabarit({ ouvert: false, id: "" })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Partir d'un gabarit ?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Le devis peut être pré-rempli avec un gabarit. Client, adresses, dates et couverts restent ceux de la demande.</p>
          <Form.Control
            as="select"
            value={choixGabarit.id}
            onChange={(e) => setChoixGabarit({ ouvert: true, id: e.target.value })}
          >
            <option value="">Choisir un gabarit…</option>
            {gabarits.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Form.Control>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => transformer(null)}>Sans gabarit</Button>
          <Button variant="success" disabled={!choixGabarit.id} onClick={() => transformer(parseInt(choixGabarit.id, 10))}>
            Transformer avec ce gabarit
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
    <ClientFicheModal
      show={ficheOpen}
      clientId={evenement?.client?.id}
      entrepriseId={evenement?.entreprise?.id}
      onClose={() => setFicheOpen(false)}
    />
    </>
  );
};

export default EvenementModal;
